import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
    Typography,
    Paper,
    CircularProgress,
    Box,
    TablePagination,
    Chip,
    Tooltip,
    Divider,
    IconButton,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Checkbox,
    ListItemText,
    Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTheme, useMediaQuery } from '@mui/material';
import AdminNav from './AdminNav';
import api from '../../utils/axiosConfig';
import moment from 'moment';

const inFlightChangesRequests = new Map();

const fetchRecentChangesOnce = (customerKey) => {
    if (!customerKey) {
        return Promise.resolve({ changes: [] });
    }

    if (inFlightChangesRequests.has(customerKey)) {
        return inFlightChangesRequests.get(customerKey);
    }

    const requestPromise = api
        .get(`/api/changes/${customerKey}`)
        .then((response) => response.data || { changes: [] })
        .finally(() => {
            inFlightChangesRequests.delete(customerKey);
        });

    inFlightChangesRequests.set(customerKey, requestPromise);
    return requestPromise;
};

const RecentChanges = () => {
    const { customerKey } = useParams();
    const [changes, setChanges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
    const [filterTypes, setFilterTypes] = useState([]); // multi-select
    const [filterAffected, setFilterAffected] = useState('');
    const [filterChangedBy, setFilterChangedBy] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
    useEffect(() => {
        let isMounted = true;

        const loadChanges = async () => {
            try {
                setLoading(true);
                const data = await fetchRecentChangesOnce(customerKey);

                if (!isMounted) {
                    return;
                }

                if (data && data.changes) {
                    setChanges(data.changes);
                } else {
                    setChanges([]);
                }
            } catch (error) {
                if (!isMounted) {
                    return;
                }

                console.error('Failed to fetch recent changes:', error);
                // Fallback to mock data if API call fails in development
                if (process.env.NODE_ENV === 'development') {
                    const mockChanges = [
                        {
                            id: 1,
                            date: new Date('2025-08-25T14:30:00'),
                            changeType: 'Permission Change',
                            field: 'UserType',
                            oldValue: 'View',
                            newValue: 'Admin',
                            changedBy: 'john.doe@example.com',
                            affectedUser: 'jane.smith@example.com',
                            readableChange: "The caregiver's user type was changed from View to Admin."
                        },
                        {
                            id: 2,
                            date: new Date('2025-08-24T10:15:00'),
                            changeType: 'User Added',
                            field: 'UserAdded',
                            oldValue: '-',
                            newValue: 'New caregiver account created',
                            changedBy: 'admin@example.com',
                            affectedUser: 'mark.johnson@example.com',
                            readableChange: 'New caregiver account was created.'
                        },
                        {
                            id: 3,
                            date: new Date('2025-08-23T16:45:00'),
                            changeType: 'User Deleted',
                            field: 'UserDeleted',
                            oldValue: 'User account active',
                            newValue: 'User account deleted',
                            changedBy: 'john.doe@example.com',
                            affectedUser: 'robert.brown@example.com',
                            readableChange: 'Caregiver account was deleted.'
                        },
                        {
                            id: 4,
                            date: new Date('2025-08-22T09:30:00'),
                            changeType: 'Information Update',
                            field: 'Email',
                            oldValue: 'old.email@example.com',
                            newValue: 'new.email@example.com',
                            changedBy: 'admin@example.com',
                            affectedUser: 'susan.williams@example.com',
                            readableChange: "The caregiver's email was changed from old.email@example.com to new.email@example.com."
                        },
                        {
                            id: 5,
                            date: new Date('2025-08-21T13:20:00'),
                            changeType: 'Permission Change',
                            field: 'UserType',
                            oldValue: 'Interactive',
                            newValue: 'View',
                            changedBy: 'john.doe@example.com',
                            affectedUser: 'david.miller@example.com',
                            readableChange: "The caregiver's user type was changed from Interactive to View."
                        }
                    ];
                    setChanges(mockChanges);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        if (customerKey) {
            loadChanges();
        }

        return () => {
            isMounted = false;
        };
    }, [customerKey]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const toggleDescription = (id) => {
        setExpandedDescriptions(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleFilterTypesChange = (event) => {
        const { value } = event.target;
        setFilterTypes(typeof value === 'string' ? value.split(',') : value);
        setPage(0);
    };

    const handleFilterTextChange = (setter) => (event) => {
        setter(event.target.value);
        setPage(0);
    };

    const clearFilters = () => {
        setFilterTypes([]);
        setFilterAffected('');
        setFilterChangedBy('');
        setPage(0);
    };

    const filteredChanges = useMemo(() => {
        return changes.filter(c => {
            const matchType = filterTypes.length === 0 || filterTypes.includes(c.changeType);
            const matchAffected = !filterAffected || (c.affectedUser || '').toLowerCase().includes(filterAffected.toLowerCase());
            const matchChangedBy = !filterChangedBy || (c.changedBy || '').toLowerCase().includes(filterChangedBy.toLowerCase());
            return matchType && matchAffected && matchChangedBy;
        });
    }, [changes, filterTypes, filterAffected, filterChangedBy]);

    const changeTypeOptions = useMemo(() => Array.from(new Set(changes.map(c => c.changeType))).sort(), [changes]);

    const pagedChanges = filteredChanges.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const getChangeTypeChip = (changeType) => {
        switch (changeType) {
            case 'Permission Change':
                return <Chip label="Permission Change" color="primary" size="small" />;
            case 'User Added':
                return <Chip label="User Added" color="success" size="small" />;
            case 'User Deleted':
                return <Chip label="User Deleted" color="error" size="small" />;
            case 'Information Update':
                return <Chip label="Information Update" color="info" size="small" />;
            case 'Contact Order':
                return <Chip label="Contact Order" color="primary" size="small" />;
            default:
                return <Chip label={changeType} color="default" size="small" />;
        }
    };

    const formatChangeDescription = (change) => {
        if (change.field === 'CallContact') {
            return change.readableChange;
        }

        // For changes that involve a "from X to Y" pattern
        if (change.oldValue && change.newValue &&
            change.changeType !== 'User Added' &&
            change.changeType !== 'User Deleted') {

            // Find the pattern "from X to Y" in the readable change
            const description = change.readableChange;
            const fromIndex = description.indexOf("from");
            const toIndex = description.indexOf("to", fromIndex);

            if (fromIndex !== -1 && toIndex !== -1) {
                // Split the text into three parts: before "from", the value after "from" and before "to", and after the value following "to"
                const beforeFrom = description.substring(0, fromIndex + 5); // +5 to include "from "
                const afterTo = description.substring(toIndex);

                return (
                    <span>
                        {beforeFrom}
                        <strong>{change.oldValue}</strong>
                        {" to "}
                        <strong>{change.newValue}</strong>
                        {afterTo.substring(afterTo.indexOf('.'))}{/* Keep only the period and anything after it */}
                    </span>
                );
            }
        }

        // For user added/deleted or any other changes that don't match the pattern
        return change.readableChange;
    };

    const renderMobileCards = (pagedChanges) => (
        <Box display="flex" flexDirection="column" gap={2} >
            {pagedChanges.map(change => {
                const fullDesc = formatChangeDescription(change);
                const plainText = typeof fullDesc === 'string' ? fullDesc : change.readableChange; // fallback for length
                const isExpanded = expandedDescriptions.has(change.id);
                const needsTruncate = plainText.length > 140; // threshold
                const displayText = isExpanded || !needsTruncate ? fullDesc : `${plainText.slice(0, 140)}...`;
                return (
                    <Paper key={change.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                            <Tooltip title={moment(change.date).format('MM/DD/YYYY h:mm:ss A')}>
                                <Typography variant="caption" color="text.secondary">
                                    {moment(change.date).format('MM/DD/YYYY h:mm A')}
                                </Typography>
                            </Tooltip>
                            {getChangeTypeChip(change.changeType)}
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Affected: <span style={{ fontWeight: 600 }}>{change.affectedUser}</span>
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                By: {change.changedBy}
                            </Typography>
                        </Box>
                        <Box mt={1}>
                            <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
                                {displayText}
                            </Typography>
                            {needsTruncate && (
                                <Box display="flex" justifyContent="flex-start" mt={0.5}>
                                    <IconButton size="small" onClick={() => toggleDescription(change.id)} aria-label={isExpanded ? 'Show less' : 'Show more'}>
                                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    </IconButton>
                                    <Typography variant="caption" sx={{ ml: -0.5, cursor: 'pointer' }} onClick={() => toggleDescription(change.id)}>
                                        {isExpanded ? 'Show less' : 'Show more'}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                );
            })}
        </Box>
    );

    const renderFilters = () => (
        <Box mb={2} display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
            <FormControl sx={{ minWidth: 180 }} size="small">
                <InputLabel id="change-type-filter-label">Type</InputLabel>
                <Select
                    labelId="change-type-filter-label"
                    multiple
                    value={filterTypes}
                    onChange={handleFilterTypesChange}
                    input={<OutlinedInput label="Type" />}
                    renderValue={(selected) => selected.join(', ')}
                >
                    {changeTypeOptions.map(type => (
                        <MenuItem key={type} value={type}>
                            <Checkbox checked={filterTypes.indexOf(type) > -1} />
                            <ListItemText primary={type} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <TextField
                size="small"
                label="Affected User"
                value={filterAffected}
                onChange={handleFilterTextChange(setFilterAffected)}
                sx={{ minWidth: 180 }}
            />
            <TextField
                size="small"
                label="Changed By"
                value={filterChangedBy}
                onChange={handleFilterTextChange(setFilterChangedBy)}
                sx={{ minWidth: 180 }}
            />
            <Button variant="outlined" color="secondary" onClick={clearFilters} disabled={!filterTypes.length && !filterAffected && !filterChangedBy} sx={{ whiteSpace: 'nowrap' }}>
                Clear Filters
            </Button>
        </Box>
    );

    // Show loading state
    if (loading) {
        return (
            <div>
                <AdminNav customerKey={customerKey} />
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                    <CircularProgress />
                    <Typography variant="body1" sx={{ ml: 2 }}>
                        Loading recent changes...
                    </Typography>
                </Box>
            </div>
        );
    }

    return (
        <div style={{ width: '100%' }}>
            <AdminNav customerKey={customerKey} />

            <Paper sx={{ maxWidth: '800px', margin: '0 auto', p: 2, mt: 2, mb:4 }}>
                <Typography variant="h5" component="h1" gutterBottom>
                    Recent Changes
                </Typography>

                {changes.length === 0 ? (
                    <Typography variant="body1">No recent changes found for this customer.</Typography>
                ) : (
                    <>
                        {renderFilters()}

                            {renderMobileCards(pagedChanges)}
                        
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={filteredChanges.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </>
                )}
            </Paper>
        </div>
    );
};

export default RecentChanges;
